import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { documentRevisionsActions } from '../../../state/ducks/documentRevisions';
import { DocumentTypeByIdForm } from '../../../state/ducks/documentRevisions/documentType/types';
import { DocumentRevision, DocumentRevisionStatus } from '../../../state/ducks/documentRevisions/types';
import Text from '../../components/Text';
import { FormContext } from '../../components/forms/FormContext';
import AutocompleteField from '../../components/forms/fields/Autocomplete/Autocomplete';
import { checkIsDocumentLHRT } from '../../documentRevision/helpers/checkDocumentGroup';
import useActionCreator from '../../hooks/useActionCreator';
import useAsync from '../../hooks/useAsync';
import { DialogHandler } from '../../hooks/useDialog';
import { styles as useStyles } from './FormListDialog/FormListDialog.styles';

interface LHRDeviationDialogProps {
  dialog: DialogHandler
  lhrtForms: DocumentTypeByIdForm[]
  isLoading: boolean
  onSelect: (lhrtRevId: string) => void
}

const LHRDeviationDialog: React.FC<LHRDeviationDialogProps> = ({
  dialog,
  lhrtForms,
  isLoading,
  onSelect,
}) => {
  const classes = useStyles();
  const [parts, setParts] = useState<DocumentRevision[]>([]);
  const fetchAvailablePartsAction = useActionCreator(documentRevisionsActions.fetchAvailableParts);

  const fetchAvailablePartsAsync = useAsync({
    onSuccess: (data: DocumentRevision[] = []) => {
      setParts(data);
    },
  });

  useEffect(() => {
      fetchAvailablePartsAsync.start(
        fetchAvailablePartsAction,
        fetchAvailablePartsAsync,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const checkForPart = (form: DocumentTypeByIdForm) => {
      const id = form.formTemplate?.schema?.find((item) => item.name === 'lhr-selected-part')?.editorConfig?.id;
      return parts.find((part) => part.document.id === id && part.status === DocumentRevisionStatus.Released);
    };

  const renderPartLabel = (form: DocumentTypeByIdForm) => {
    const partDocRev = checkForPart(form);
      if (!partDocRev) {
        return `${form.document.docId} - ${form.name}`;
      }
      return `(${partDocRev?.document.docId} rev ${partDocRev?.displayRevision} - ${partDocRev?.name}) ${form.name}`;
    };

  const renderOptions = () => {
      const LHRForms = lhrtForms.filter((form) => {
        const partDocRev = checkForPart(form);
        const isLHRT = checkIsDocumentLHRT(form.document.documentType?.groupOptions);
        return (isLHRT && partDocRev) || (!isLHRT && !partDocRev);
      });

      return LHRForms.map((form) => ({
        label: renderPartLabel(form),
        value: form.id,
      }));
    };

  return (
    <Dialog
      open={dialog.isOpen}
      onClose={dialog.close}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <Text translation="deviation.select.lhrt.title" />
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        {isLoading ? (
          <Box className={classes.loading}>
            <CircularProgress size={20} />
            <Text translation="common.loading" />
          </Box>
        ) : (
          <FormContext.Provider value={{ submitOnChange: false }}>
            <Formik initialValues={{}} onSubmit={() => undefined}>
              {() => (
                <AutocompleteField
                  name="selectLHRT"
                  size="sm"
                  portal
                  autocompleteProps={{ id: 'deviation-dialog-lhr-select' }}
                  options={renderOptions()}
                  onClick={(option: { value: string }) => onSelect(option.value)}
                  label="deviation.select.lhrt.label"
                />
              )}
            </Formik>
          </FormContext.Provider>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={dialog.close}
          color="secondary"
          className={classes.cancelButton}
        >
          <Text translation="common.cancel" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LHRDeviationDialog;
